import { getFieldPageAndRect, getFieldTooltip } from "./pdfFieldGeometry";

/**
 * Builds the title of an addendum section out of what the PDF itself declares,
 * e.g. "Addendum to I-589, Page 5, Part B, Item 1.A."
 *
 * The raw material is the field's tooltip (/TU), which on USCIS forms starts
 * with the question's own outline. Two shapes show up in practice:
 *
 *   I-589: "Part B. 1. A. Have you, your family, ... ever experienced harm ...?"
 *          "Part C. 2. A. and 2. B.  If Yes, to either or both questions ..."
 *          "Supplement B. Additional Information About Your Claim to Asylum. ..."
 *   I-485: "Part 9. General Eligibility and Inadmissibility Grounds. Public
 *           Charge. 66. If your answer to Item Number 64. is "Yes," list ..."
 *
 * Note "Part." with a trailing period: both spellings occur, and requiring the
 * bare one alone leaves most of an I-589 unparsed.
 *
 * The outline tokens are read in groups, because the number right after the
 * section is not always the item - on the I-589 it is a SUBSECTION, and the item
 * follows the subsection's title:
 *
 *   "Part. A. 3. Information About Your Background. 1. List your last address..."
 *    section ^  ^ subsection                        ^ item
 *
 * so the last group before the question's prose is the item and an earlier one
 * refines the section ("Part A.3"). A single group is always the item, which is
 * what the "Part B. 1. A." and I-485 shapes need.
 *
 * Whatever the outline yields, the label also quotes the tooltip's own trailing
 * "Enter ..." clause, which is the one part that names the individual input
 * rather than the question it belongs to. The field's internal name is the last
 * resort, and only when there is no tooltip at all.
 */

/** "Part B." / "Part. A." / "Part 9." / "Supplement B." at the start. */
const SECTION_REGEX = /^\s*(Part|Supplement)\.?\s+(\d+|[A-Za-z])\.\s*/;

/** A single outline token: "1. " or "A. ". */
const OUTLINE_TOKEN_REGEX = /^(\d+|[A-Za-z])\.\s*/;

/** What may sit between two item references: "2. A. and 2. B." */
const CONNECTOR_REGEX = /^(and\/or|and|or|&|,)\s*/i;

/**
 * The last segment of a fully qualified AcroForm name.
 * "form1[0].#subform[20].Pt8Line68d_Column1Row1[0]" -> "Pt8Line68d_Column1Row1"
 */
const getPartialName = (field) => {
    const full = field.acroField.getFullyQualifiedName?.() || field.getName?.() || "";
    const last = full.split(".").pop() || full;
    return last.replace(/\[\d+\]$/, "");
};

/**
 * Reads one leading outline group, e.g. "1. A. " -> [1.A.] and
 * "2. A. and 2. B. " -> [2.A., 2.B.]. Stops as soon as the question's prose
 * begins, since only single letters/numbers followed by a period qualify.
 * Also returns what is left, so the caller can look for a further group.
 */
const parseOutlineItems = (text) => {
    const items = [];
    let rest = text;
    let connector = "";

    for (;;) {
        const token = rest.match(OUTLINE_TOKEN_REGEX);
        if (!token) break;

        const value = token[1];
        rest = rest.slice(token[0].length);

        if (/^\d+$/.test(value)) {
            items.push({ number: value, letters: [], connector });
            connector = "";
        } else if (items.length && !connector) {
            // A letter right after a number refines it: "1." + "A." -> "1.A."
            items[items.length - 1].letters.push(value.toUpperCase());
        } else {
            break;
        }

        const link = rest.match(CONNECTOR_REGEX);
        if (link) {
            connector = link[1].toLowerCase();
            rest = rest.slice(link[0].length);
        }
    }

    return { items, rest };
};

/** Outline groups worth reading: one subsection at most, then the item. */
const MAX_OUTLINE_GROUPS = 2;

/** How far into the tooltip an outline group may still turn up. */
const OUTLINE_SCAN_LIMIT = 260;

/** Skips to where the next outline group could start: after this sentence. */
const nextSentence = (text) => {
    const match = text.match(/[.?!]\s+/);
    return match ? text.slice(match.index + match[0].length) : "";
};

/**
 * Collects the outline groups that precede the question's prose. Titles sit
 * between them ("A. 3. Information About Your Background. 1. List your..."),
 * so a group that yields nothing just advances to the next sentence.
 */
const parseOutlineGroups = (text) => {
    const groups = [];
    let rest = text.slice(0, OUTLINE_SCAN_LIMIT);

    while (rest && groups.length < MAX_OUTLINE_GROUPS) {
        const { items, rest: after } = parseOutlineItems(rest);
        if (items.length) {
            groups.push(items);
            rest = after;
        } else {
            rest = nextSentence(rest);
        }
    }

    return groups;
};

const renderItem = (item) => `${item.number}${item.letters.length ? `.${item.letters.join(".")}` : ""}`;

const formatItems = (items) => {
    if (items.length === 0) return "";

    const rendered = items
        .map((item, index) => (index === 0 ? `${renderItem(item)}.` : `${item.connector || "and"} ${renderItem(item)}.`))
        .join(" ");

    return `${items.length > 1 ? "Items" : "Item"} ${rendered}`;
};

/**
 * Fallback for tooltips whose section is followed by prose rather than by the
 * item outline (the I-485 shape): the first standalone numeric clause.
 * Deliberately not matching "Item Number N" - in those same tooltips it points
 * at a DIFFERENT item than the field's own.
 */
const findNumericClause = (text) => {
    const match = text.match(/\.\s+(\d+)\.(?=\s)/);
    return match ? `Item ${match[1]}.` : "";
};

/** Longest descriptor kept, so a section title never becomes the whole label. */
const DESCRIPTOR_MAX = 90;

/**
 * The tooltip's trailing "Enter ..." clause. A /TU describes the whole question,
 * which several fields share; only this clause names the individual input
 * ("Enter Number and Street... Line 1 of 2."). The LAST one is the field's own,
 * since earlier ones belong to the surrounding instructions.
 */
const clampDescriptor = (text) => {
    const single = text.replace(/\s+/g, " ").trim();
    const capitalized = single.charAt(0).toUpperCase() + single.slice(1);
    if (capitalized.length <= DESCRIPTOR_MAX) return capitalized.replace(/[\s.:;,]+$/, "");

    const clipped = capitalized.slice(0, DESCRIPTOR_MAX);
    const boundary = clipped.lastIndexOf(" ");
    return `${(boundary > 0 ? clipped.slice(0, boundary) : clipped).replace(/[\s.:;,]+$/, "")}...`;
};

const parseDescriptor = (tooltip) => {
    const regex = /(?:^|\s)Enter\s+/g;
    let start = -1;
    let match;
    while ((match = regex.exec(tooltip))) start = match.index + match[0].length;

    return start < 0 ? "" : clampDescriptor(tooltip.slice(start));
};

/**
 * When a tooltip has no "Enter ..." clause and the outline gave us nothing, its
 * opening still beats printing the field's internal name. It is clamped rather
 * than split into sentences because these tooltips spell abbreviations out with
 * periods ("A. Number", "U. S."), which no sentence split survives. A one-word
 * tooltip IS the internal name, though ("PDF417BarCode1"), so it is skipped.
 */
const parseTooltipOpening = (tooltip) => (tooltip.trim().includes(" ") ? clampDescriptor(tooltip) : "");

/**
 * Last resort: the field's internal name.
 * "Pt8Line68d_Column1Row1" -> Part 8 / Item 68.d.
 * "PBL3A_TextField"        -> Part B / Item 3.A.
 */
const parsePartialName = (partialName) => {
    const uscis = partialName.match(/^Pt(\d+)Line(\d+)([A-Za-z])?/i);
    if (uscis) {
        return {
            section: `Part ${uscis[1]}`,
            item: `Item ${uscis[2]}${uscis[3] ? `.${uscis[3].toUpperCase()}` : ""}.`,
        };
    }

    const legacy = partialName.match(/^P([A-Z])L?(\d+)([A-Z])?_/);
    if (legacy) {
        return {
            section: `Part ${legacy[1]}`,
            item: `Item ${legacy[2]}${legacy[3] ? `.${legacy[3].toUpperCase()}` : ""}.`,
        };
    }

    return { section: "", item: "" };
};

/**
 * Where a field sits in the form's outline, as read from its tooltip (falling
 * back to its internal name).
 *
 * @param {PDFField} field
 * @returns {{ section: string, item: string, descriptor: string, partialName: string }} e.g.
 *   { section: "Part A.3", item: "Item 1.", descriptor: "Number and Street", ... }
 */
export const getFieldLocation = (field) => {
    const tooltip = getFieldTooltip(field);
    const partialName = getPartialName(field);
    const fromName = parsePartialName(partialName);

    let section = "";
    let item = "";

    const sectionMatch = tooltip.match(SECTION_REGEX);
    if (sectionMatch) {
        const kind = sectionMatch[1];
        const reference = /^\d+$/.test(sectionMatch[2]) ? sectionMatch[2] : sectionMatch[2].toUpperCase();

        const rest = tooltip.slice(sectionMatch[0].length);
        const groups = parseOutlineGroups(rest);

        // The last group is the item; anything before it refines the section.
        const refinement = groups
            .slice(0, -1)
            .map((group) => group.map(renderItem).join("."))
            .filter(Boolean)
            .join(".");

        section = `${kind} ${reference}${refinement ? `.${refinement}` : ""}`;
        item = formatItems(groups[groups.length - 1] || []) || findNumericClause(rest);
    }

    if (!section) section = fromName.section;
    if (!item) item = fromName.item;

    const descriptor = parseDescriptor(tooltip) || (item ? "" : parseTooltipOpening(tooltip));

    return { section, item, descriptor, partialName };
};

/**
 * Joins the pieces of an addendum title.
 * "Addendum to I-589, Page 4, Part A.3, Item 1: Number and Street."
 */
const composeLabel = ({ formCode, pageNumber, section, item, descriptor, fallback }) => {
    const segments = [`Addendum${formCode ? ` to ${formCode}` : ""}`];
    if (pageNumber > 0) segments.push(`Page ${pageNumber}`);

    if (section) segments.push(section);
    // The item's own period would collide with the colon introducing the descriptor.
    if (item) segments.push(descriptor ? item.replace(/\.$/, "") : item);

    if (!section && !item && !descriptor && fallback) segments.push(fallback);

    const located = segments.join(", ");
    const label = descriptor ? `${located}: ${descriptor}` : located;

    return label.endsWith(".") ? label : `${label}.`;
};

/**
 * @param {{ formCode?: string, pageNumber?: number, field: PDFField }} params
 * @returns {string} e.g.
 *   "Addendum to I-589, Page 4, Part A.3, Item 1: Number and Street."
 *   "Addendum to I-589, Page 7, Part C, Items 2.A. and 2.B."
 */
export const buildAddendumLabel = ({ formCode, pageNumber, field }) => {
    const { section, item, descriptor, partialName } = getFieldLocation(field);
    // The internal name only helps when the PDF gave us nothing to read.
    return composeLabel({ formCode, pageNumber, section, item, descriptor, fallback: `Field ${partialName}` });
};

/**
 * Title of an addendum written by hand. The reference is printed as the user
 * wrote it; without one it is a general addendum to the form: "Addendum to I-589."
 *
 * @param {{ formCode?: string, pageNumber?: number, reference?: string }} params
 */
export const buildManualAddendumLabel = ({ formCode, pageNumber, reference }) =>
    composeLabel({ formCode, pageNumber: reference ? pageNumber : 0, section: reference?.replace(/\s+/g, " ").trim() });

/**
 * The sections (and their items) a manual addendum can be attached to, in
 * reading order. Fields the outline says nothing about are skipped.
 *
 * @param {PDFField[]} fields
 * @param {PDFPage[]} pages
 * @returns {Array<{ section: string, pageNumber: number, items: Array<{ item: string, pageNumber: number, fieldName: string, descriptor: string }> }>}
 */
export const collectFormSections = (fields, pages) => {
    const located = [];

    fields.forEach((field) => {
        try {
            const widgetInfo = getFieldPageAndRect(field, pages);
            if (!widgetInfo || widgetInfo.pageIndex < 0) return;

            const { section, item, descriptor } = getFieldLocation(field);
            if (!section) return;

            located.push({ section, item, descriptor, fieldName: field.getName(), pageIndex: widgetInfo.pageIndex, rect: widgetInfo.rect });
        } catch (e) {
            // A field we cannot read just does not offer a section.
        }
    });

    // Reading order: page, then top->bottom, left->right.
    located.sort((a, b) => a.pageIndex - b.pageIndex || b.rect.y - a.rect.y || a.rect.x - b.rect.x);

    const sections = new Map();
    located.forEach(({ section, item, descriptor, fieldName, pageIndex }) => {
        if (!sections.has(section)) {
            sections.set(section, { section, pageNumber: pageIndex + 1, items: [], seenItems: new Set() });
        }
        const entry = sections.get(section);
        if (item && !entry.seenItems.has(item)) {
            entry.seenItems.add(item);
            entry.items.push({ item, pageNumber: pageIndex + 1, fieldName, descriptor });
        }
    });

    return [...sections.values()].map(({ seenItems, ...rest }) => rest);
};

/**
 * References offered while typing a manual addendum's reference: every section
 * and every item of it, e.g. "Part 2" and "Part 2, Item 3.A.".
 *
 * @returns {Array<{ value: string, pageNumber: number, fieldName?: string, hint: string }>}
 */
export const buildReferenceSuggestions = (sections) =>
    sections.flatMap(({ section, pageNumber, items }) => [
        { value: section, pageNumber, hint: `Page ${pageNumber}` },
        ...items.map(({ item, pageNumber: itemPage, fieldName, descriptor }) => ({
            value: `${section}, ${item}`,
            pageNumber: itemPage,
            fieldName,
            hint: [`Page ${itemPage}`, descriptor].filter(Boolean).join(" · "),
        })),
    ]);

const normalizeReference = (value) =>
    String(value || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .replace(/[\s.,;:]+$/, "")
        .trim();

/** The suggestion a typed reference stands for, ignoring case, spacing and trailing punctuation. */
export const findReferenceSuggestion = (reference, suggestions) => {
    const wanted = normalizeReference(reference);
    return wanted ? suggestions.find((x) => normalizeReference(x.value) === wanted) || null : null;
};
